import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';
import { useDialog } from '../useDialog';

// ── Constants ─────────────────────────────────────────────────────────────────
const PART_LABELS = {
  cervical: 'Cervical (Neck)', upperback: 'Upper Back', middleback: 'Middle Back',
  lowerback: 'Lower Back & Pelvis', coccyx: 'Coccyx', chest: 'Chest',
  heart: 'Heart', digestive: 'Digestive', abdomen: 'Abdomen',
  renal: 'Renal', reproductive: 'Reproductive', lung: 'Lungs',
  shoulder: 'Shoulder', arm: 'Arm', wrist: 'Wrist',
  knee: 'Knee', thigh: 'Thigh', leg: 'Leg', calf: 'Calf',
  ankle: 'Ankle', foot: 'Foot',
};


const PART_COLOR = {
  cervical:'#1565c0', upperback:'#1976d2', middleback:'#1e88e5', lowerback:'#2196f3', coccyx:'#42a5f5',
  chest:'#6a1b9a', shoulder:'#7b1fa2', arm:'#8e24aa', wrist:'#9c27b0',
  knee:'#2e7d32', thigh:'#388e3c', leg:'#43a047', calf:'#4caf50', ankle:'#66bb6a', foot:'#81c784',
  heart:'#c62828', digestive:'#e53935', abdomen:'#ef5350', renal:'#ff7043', reproductive:'#ff8f00', lung:'#795548',
};

const CONDITION_OPTIONS = {
  painSeverity:   ['mild','moderate','severe'],
  painType:       ['sharp','dull','aching','burning','throbbing','radiating','stabbing'],
  painDuration:   ['acute','subacute','chronic'],
  mobilityImpact: ['mild','moderate','severe'],
  symptoms:       ['numbness','stiffness','weakness','spasm','instability'],
};

const DAYS_OF_WEEK = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

const SORT_OPTIONS = [
  { value: 'bodyPart_asc', label: 'Body Part A→Z' },
  { value: 'name_asc',     label: 'Name A→Z' },
  { value: 'name_desc',    label: 'Name Z→A' },
  { value: 'createdAt_desc',label: 'Newest First' },
  { value: 'priority_desc', label: 'Priority High→Low' },
];

const emptyMapping = () => ({
  name: '', bodyPart: '', bodyPartLabel: '',
  conditions: { painSeverity: [], painType: [], painDuration: [], mobilityImpact: [], symptoms: [], gradeMin: '', gradeMax: '' },
  sequences: [], recommendations: [],
  mythsessions: 0, maintsessions: 0, preventivesessions: 0,
  isActive: true, source: 'manual', notes: '', priority: 0,
});

const emptySeq  = () => ({ name:'', repetition:10, duration:5, days:7, workoutDays:[], remarks:'', vlink:'' });
const emptyRec  = () => ({ recommendation:'', qty:'', nod:7, not:1, rtype:'', remarks:'' });

// ── Helpers ───────────────────────────────────────────────────────────────────
function partColor(part) { return PART_COLOR[part] || '#607d8b'; }
function conditionTags(c = {}) {
  return [
    ...(c.painSeverity  || []).map(v => ({ label: v, color: '#e65100' })),
    ...(c.painDuration  || []).map(v => ({ label: v, color: '#1565c0' })),
    ...(c.painType      || []).map(v => ({ label: v, color: '#6a1b9a' })),
    ...(c.mobilityImpact|| []).map(v => ({ label: `mob: ${v}`, color: '#2e7d32' })),
    ...(c.symptoms      || []).map(v => ({ label: v, color: '#00838f' })),
    ...(c.gradeMin != null && c.gradeMin !== '' ? [{ label: `grade ≥${c.gradeMin}`, color: '#555' }] : []),
    ...(c.gradeMax != null && c.gradeMax !== '' ? [{ label: `grade ≤${c.gradeMax}`, color: '#555' }] : []),
  ];
}
function sortMappings(list, sortKey) {
  const [field, dir] = sortKey.split('_');
  return [...list].sort((a, b) => {
    const av = field === 'createdAt' ? new Date(a[field]) : (a[field] || '');
    const bv = field === 'createdAt' ? new Date(b[field]) : (b[field] || '');
    if (av < bv) return dir === 'asc' ? -1 : 1;
    if (av > bv) return dir === 'asc' ?  1 : -1;
    return 0;
  });
}

// ── Chip multi-select ─────────────────────────────────────────────────────────
function ChipSelect({ label, options, selected, onChange, color = '#1976d2' }) {
  const safe   = Array.isArray(selected) ? selected : [];
  const toggle = v => onChange(safe.includes(v) ? safe.filter(x => x !== v) : [...safe, v]);
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>{label}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {options.map(o => {
          const on = safe.includes(o);
          return (
            <button key={o} onClick={() => toggle(o)} style={{
              padding: '4px 12px', borderRadius: 20, border: `1.5px solid ${on ? color : '#d1d5db'}`,
              background: on ? color : '#fff', color: on ? '#fff' : '#374151',
              fontSize: 12, fontWeight: on ? 700 : 400, cursor: 'pointer', transition: 'all 0.15s',
            }}>{o}</button>
          );
        })}
      </div>
    </div>
  );
}

// ── Mapping card ──────────────────────────────────────────────────────────────
function MappingCard({ m, selected, onSelect, onEdit, onDelete, onToggleActive }) {
  const tags = conditionTags(m.conditions);
  const color = partColor(m.bodyPart);
  return (
    <div style={{
      background: '#fff', borderRadius: 16, border: `1.5px solid ${selected ? color : '#e8edf5'}`,
      boxShadow: selected ? `0 0 0 3px ${color}22, 0 4px 16px rgba(0,0,0,.08)` : '0 2px 8px rgba(0,0,0,.06)',
      transition: 'all 0.18s', overflow: 'hidden', display: 'flex', flexDirection: 'column',
    }}>
      {/* Card top color strip */}
      <div style={{ height: 4, background: `linear-gradient(90deg, ${color}, ${color}88)` }} />

      <div style={{ padding: '14px 16px 12px', flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <input type="checkbox" checked={selected} onChange={onSelect}
            style={{ marginTop: 3, width: 16, height: 16, accentColor: color, flexShrink: 0, cursor: 'pointer' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 5 }}>
              <span style={{ background: color, color: '#fff', borderRadius: 10, padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>
                {PART_LABELS[m.bodyPart] || m.bodyPart}
              </span>
              {m.source === 'generated' && (
                <span style={{ background: '#e0f2fe', color: '#0369a1', borderRadius: 10, padding: '2px 8px', fontSize: 10, fontWeight: 600 }}>⚡ Auto</span>
              )}
              <span onClick={onToggleActive} style={{
                background: m.isActive ? '#dcfce7' : '#f1f5f9', color: m.isActive ? '#166534' : '#64748b',
                borderRadius: 10, padding: '2px 8px', fontSize: 10, fontWeight: 600, cursor: 'pointer',
              }}>
                {m.isActive ? '● Active' : '○ Inactive'}
              </span>
            </div>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#111827', lineHeight: 1.3 }}>{m.name}</div>
          </div>
        </div>

        {/* Condition tags */}
        {tags.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {tags.map((t, i) => (
              <span key={i} style={{ background: `${t.color}18`, color: t.color, borderRadius: 8, padding: '2px 8px', fontSize: 10, fontWeight: 600 }}>{t.label}</span>
            ))}
          </div>
        )}

        {/* Stats */}
        <div style={{ display: 'flex', gap: 12, fontSize: 12, color: '#6b7280', flexWrap: 'wrap' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 14 }}>🏋️</span>{m.sequences?.length || 0} exercises
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 14 }}>💊</span>{m.recommendations?.length || 0} recommendations
          </span>
        </div>

        {/* Session counts */}
        {(m.mythsessions > 0 || m.maintsessions > 0 || m.preventivesessions > 0) && (
          <div style={{ display: 'flex', gap: 8, fontSize: 11, flexWrap: 'wrap' }}>
            {m.mythsessions > 0 && <span style={{ background: '#ede9fe', color: '#5b21b6', borderRadius: 8, padding: '2px 8px', fontWeight: 600 }}>📋 {m.mythsessions} Doctor</span>}
            {m.maintsessions > 0 && <span style={{ background: '#e0f2fe', color: '#0369a1', borderRadius: 8, padding: '2px 8px', fontWeight: 600 }}>🔄 {m.maintsessions} Sr.Therapist</span>}
            {m.preventivesessions > 0 && <span style={{ background: '#dcfce7', color: '#166534', borderRadius: 8, padding: '2px 8px', fontWeight: 600 }}>🛡 {m.preventivesessions} Therapist</span>}
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={{ padding: '10px 16px', borderTop: '1px solid #f0f4f8', display: 'flex', justifyContent: 'flex-end', gap: 8, background: '#fafbfc' }}>
        <button onClick={onEdit} style={{ padding: '6px 14px', border: '1.5px solid #d1d5db', borderRadius: 8, background: '#fff', color: '#374151', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          ✏️ Edit
        </button>
        <button onClick={onDelete} style={{ padding: '6px 14px', border: '1.5px solid #fecaca', borderRadius: 8, background: '#fff', color: '#dc2626', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          🗑 Delete
        </button>
      </div>
    </div>
  );
}

// ── Sequence editor table ─────────────────────────────────────────────────────
function SequenceEditor({ sequences, onChange }) {
  const update = (i, field, val) => {
    const next = sequences.map((s, idx) => idx === i ? { ...s, [field]: val } : s);
    onChange(next);
  };
  const remove = i => onChange(sequences.filter((_, idx) => idx !== i));
  const add    = () => onChange([...sequences, emptySeq()]);

  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#f7f9fc' }}>
              {['Exercise Name','Reps','Duration (min)','Days','Workout Days','Remarks','Video Link',''].map(h => (
                <th key={h} style={{ padding: '8px 10px', fontWeight: 700, color: '#6b7280', borderBottom: '2px solid #e8edf5', whiteSpace: 'nowrap', textAlign: 'left' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sequences.map((s, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #f0f4f8' }}>
                <td style={{ padding: '6px 8px' }}>
                  <input value={s.name} onChange={e => update(i,'name',e.target.value)} placeholder="Exercise name"
                    style={{ width: 150, border: '1.5px solid #e2e8f0', borderRadius: 6, padding: '4px 8px', fontSize: 12, outline: 'none' }} />
                </td>
                <td style={{ padding: '6px 8px' }}>
                  <input type="number" value={s.repetition} onChange={e => update(i,'repetition',Number(e.target.value))} min={1}
                    style={{ width: 52, border: '1.5px solid #e2e8f0', borderRadius: 6, padding: '4px', fontSize: 12, textAlign: 'center', outline: 'none' }} />
                </td>
                <td style={{ padding: '6px 8px' }}>
                  <input type="number" value={s.duration} onChange={e => update(i,'duration',Number(e.target.value))} min={1}
                    style={{ width: 60, border: '1.5px solid #e2e8f0', borderRadius: 6, padding: '4px', fontSize: 12, textAlign: 'center', outline: 'none' }} />
                </td>
                <td style={{ padding: '6px 8px' }}>
                  <input type="number" value={s.days} onChange={e => update(i,'days',Number(e.target.value))} min={1}
                    style={{ width: 52, border: '1.5px solid #e2e8f0', borderRadius: 6, padding: '4px', fontSize: 12, textAlign: 'center', outline: 'none' }} />
                </td>
                <td style={{ padding: '6px 8px' }}>
                  <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    {DAYS_OF_WEEK.map(d => {
                      const on = (s.workoutDays || []).includes(d);
                      return (
                        <button key={d} onClick={() => {
                          const wd = on ? s.workoutDays.filter(x => x !== d) : [...(s.workoutDays||[]), d];
                          update(i, 'workoutDays', wd);
                        }} style={{
                          padding: '2px 5px', fontSize: 10, borderRadius: 4, cursor: 'pointer',
                          border: `1px solid ${on ? '#1976d2' : '#d1d5db'}`,
                          background: on ? '#1976d2' : '#fff', color: on ? '#fff' : '#6b7280', fontWeight: on ? 700 : 400,
                        }}>{d}</button>
                      );
                    })}
                  </div>
                </td>
                <td style={{ padding: '6px 8px' }}>
                  <input value={s.remarks} onChange={e => update(i,'remarks',e.target.value)} placeholder="Notes"
                    style={{ width: 100, border: '1.5px solid #e2e8f0', borderRadius: 6, padding: '4px 8px', fontSize: 12, outline: 'none' }} />
                </td>
                <td style={{ padding: '6px 8px' }}>
                  <input value={s.vlink} onChange={e => update(i,'vlink',e.target.value)} placeholder="https://..."
                    style={{ width: 100, border: '1.5px solid #e2e8f0', borderRadius: 6, padding: '4px 8px', fontSize: 12, outline: 'none' }} />
                </td>
                <td style={{ padding: '6px 8px' }}>
                  <button onClick={() => remove(i)} style={{ background: '#fee2e2', border: 'none', color: '#dc2626', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 13 }}>✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button onClick={add} style={{ marginTop: 10, padding: '7px 16px', border: '1.5px dashed #d1d5db', borderRadius: 8, background: '#f9fafb', color: '#374151', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
        + Add Exercise
      </button>
    </div>
  );
}

// ── Recommendation editor table ───────────────────────────────────────────────
function RecommEditor({ recommendations, bodyPart, onChange }) {
  const update = (i, field, val) => onChange(recommendations.map((r, idx) => idx === i ? { ...r, [field]: val } : r));
  const remove = i => onChange(recommendations.filter((_, idx) => idx !== i));
  const add    = () => onChange([...recommendations, { ...emptyRec(), rtype: bodyPart }]);

  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#f7f9fc' }}>
              {['Recommendation','Qty/Dose','Times/Day','Days','Notes',''].map(h => (
                <th key={h} style={{ padding: '8px 10px', fontWeight: 700, color: '#6b7280', borderBottom: '2px solid #e8edf5', whiteSpace: 'nowrap', textAlign: 'left' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {recommendations.map((r, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #f0f4f8' }}>
                <td style={{ padding: '6px 8px' }}>
                  <input value={r.recommendation} onChange={e => update(i,'recommendation',e.target.value)} placeholder="e.g. Ice therapy"
                    style={{ width: 180, border: '1.5px solid #e2e8f0', borderRadius: 6, padding: '4px 8px', fontSize: 12, outline: 'none' }} />
                </td>
                <td style={{ padding: '6px 8px' }}>
                  <input value={r.qty} onChange={e => update(i,'qty',e.target.value)} placeholder="e.g. 15 min"
                    style={{ width: 80, border: '1.5px solid #e2e8f0', borderRadius: 6, padding: '4px 8px', fontSize: 12, outline: 'none' }} />
                </td>
                <td style={{ padding: '6px 8px' }}>
                  <input type="number" value={r.not} onChange={e => update(i,'not',Number(e.target.value))} min={1}
                    style={{ width: 52, border: '1.5px solid #e2e8f0', borderRadius: 6, padding: '4px', fontSize: 12, textAlign: 'center', outline: 'none' }} />
                </td>
                <td style={{ padding: '6px 8px' }}>
                  <input type="number" value={r.nod} onChange={e => update(i,'nod',Number(e.target.value))} min={1}
                    style={{ width: 52, border: '1.5px solid #e2e8f0', borderRadius: 6, padding: '4px', fontSize: 12, textAlign: 'center', outline: 'none' }} />
                </td>
                <td style={{ padding: '6px 8px' }}>
                  <input value={r.remarks} onChange={e => update(i,'remarks',e.target.value)} placeholder="Notes"
                    style={{ width: 110, border: '1.5px solid #e2e8f0', borderRadius: 6, padding: '4px 8px', fontSize: 12, outline: 'none' }} />
                </td>
                <td style={{ padding: '6px 8px' }}>
                  <button onClick={() => remove(i)} style={{ background: '#fee2e2', border: 'none', color: '#dc2626', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 13 }}>✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button onClick={add} style={{ marginTop: 10, padding: '7px 16px', border: '1.5px dashed #d1d5db', borderRadius: 8, background: '#f9fafb', color: '#374151', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
        + Add Recommendation
      </button>
    </div>
  );
}

// ── Mapping editor modal ──────────────────────────────────────────────────────
const EMPTY_COND = { painSeverity: [], painType: [], painDuration: [], mobilityImpact: [], symptoms: [], gradeMin: '', gradeMax: '' };

function MappingModal({ mapping, onClose, onSave }) {
  const [form, setForm] = useState(() => {
    const base = mapping || emptyMapping();
    return { ...base, conditions: { ...EMPTY_COND, ...(base.conditions || {}) } };
  });
  const [tab, setTab]     = useState('info');
  const [saving, setSaving] = useState(false);
  const { dialogNode, showAlert } = useDialog();

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));
  const setCond = (field, val) => setForm(f => ({ ...f, conditions: { ...f.conditions, [field]: val } }));

  const handleSave = async () => {
    if (!form.name.trim()) { showAlert('Mapping name is required', 'warning'); return; }
    if (!form.bodyPart)    { showAlert('Body part is required', 'warning'); return; }
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (e) {
      showAlert('Save failed: ' + (e?.response?.data || e?.message), 'error');
    } finally { setSaving(false); }
  };

  const TABS = [
    { key: 'info',    label: 'ℹ️ Info'           },
    { key: 'cond',    label: '🔍 Conditions'      },
    { key: 'seqs',    label: `🏋️ Exercises (${form.sequences.length})` },
    { key: 'recs',    label: `💊 Recommendations (${form.recommendations.length})` },
  ];

  const inp = (placeholder, value, onChange, type = 'text', extraStyle = {}) => (
    <input type={type} placeholder={placeholder} value={value ?? ''} onChange={e => onChange(type === 'number' ? Number(e.target.value) : e.target.value)}
      style={{ width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '9px 12px', fontSize: 13, outline: 'none', boxSizing: 'border-box', ...extraStyle }} />
  );

  return (
    <>
    {dialogNode}
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: '#fff', borderRadius: 20, width: '100%', maxWidth: 860, maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,.22)' }}>

        {/* Modal header */}
        <div style={{ padding: '20px 24px 0', borderBottom: '2px solid #e8edf5' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#111827' }}>
              {mapping?._id ? '✏️ Edit Mapping' : '+ New Mapping'}
            </h2>
            <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', background: '#f1f5f9', cursor: 'pointer', fontSize: 16, color: '#64748b' }}>✕</button>
          </div>
          <div style={{ display: 'flex', gap: 0, overflowX: 'auto' }}>
            {TABS.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} style={{
                padding: '10px 20px', border: 'none', background: 'transparent', cursor: 'pointer',
                borderBottom: tab === t.key ? '3px solid #1976d2' : '3px solid transparent',
                color: tab === t.key ? '#1976d2' : '#6b7280', fontWeight: tab === t.key ? 700 : 500,
                fontSize: 13, whiteSpace: 'nowrap', marginBottom: -2,
              }}>{t.label}</button>
            ))}
          </div>
        </div>

        {/* Tab content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

          {/* ── Info tab ── */}
          {tab === 'info' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6 }}>Mapping Name *</label>
                {inp('e.g. Cervical Chronic Pain Protocol', form.name, v => set('name', v))}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6 }}>Body Part *</label>
                <select value={form.bodyPart} onChange={e => set('bodyPart', e.target.value)}
                  style={{ width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '9px 12px', fontSize: 13, outline: 'none', background: '#fff' }}>
                  <option value="">— Select body part —</option>
                  {Object.entries(PART_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6 }}>Priority (higher = preferred)</label>
                {inp('0', form.priority, v => set('priority', v), 'number')}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6 }}>Doctor Sessions</label>
                {inp('0', form.mythsessions, v => set('mythsessions', v), 'number')}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6 }}>Sr. Therapist Sessions</label>
                {inp('0', form.maintsessions, v => set('maintsessions', v), 'number')}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6 }}>Therapist Sessions</label>
                {inp('0', form.preventivesessions, v => set('preventivesessions', v), 'number')}
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6 }}>Notes</label>
                <textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Internal notes about this mapping…"
                  style={{ width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '9px 12px', fontSize: 13, outline: 'none', minHeight: 70, boxSizing: 'border-box', resize: 'vertical' }} />
              </div>
              <div style={{ gridColumn: '1/-1', display: 'flex', alignItems: 'center', gap: 10 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Active</label>
                <button onClick={() => set('isActive', !form.isActive)} style={{
                  width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
                  background: form.isActive ? '#22c55e' : '#d1d5db', position: 'relative', transition: 'background 0.2s',
                }}>
                  <span style={{ position: 'absolute', top: 3, left: form.isActive ? 22 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,.3)' }} />
                </button>
              </div>
            </div>
          )}

          {/* ── Conditions tab ── */}
          {tab === 'cond' && (
            <div>
              <div style={{ padding: '10px 14px', background: '#f0f9ff', borderRadius: 10, border: '1px solid #bae6fd', fontSize: 12, color: '#0369a1', marginBottom: 18 }}>
                ℹ️ Conditions define when this mapping is suggested. A patient consultation matches if <strong>any</strong> of the selected values in each category overlap.
              </div>
              <ChipSelect label="Pain Severity" options={CONDITION_OPTIONS.painSeverity} selected={form.conditions.painSeverity} onChange={v => setCond('painSeverity', v)} color="#e65100" />
              <ChipSelect label="Pain Duration" options={CONDITION_OPTIONS.painDuration}  selected={form.conditions.painDuration}  onChange={v => setCond('painDuration',  v)} color="#1565c0" />
              <ChipSelect label="Pain Type"     options={CONDITION_OPTIONS.painType}      selected={form.conditions.painType}      onChange={v => setCond('painType',      v)} color="#6a1b9a" />
              <ChipSelect label="Mobility Impact" options={CONDITION_OPTIONS.mobilityImpact} selected={form.conditions.mobilityImpact} onChange={v => setCond('mobilityImpact', v)} color="#2e7d32" />
              <ChipSelect label="Symptoms"       options={CONDITION_OPTIONS.symptoms}      selected={form.conditions.symptoms}      onChange={v => setCond('symptoms',      v)} color="#00838f" />
              <div style={{ display: 'flex', gap: 16, marginTop: 4 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 6 }}>Grade Min (1–10)</label>
                  <input type="number" min={1} max={10} value={form.conditions.gradeMin ?? ''} onChange={e => setCond('gradeMin', e.target.value === '' ? '' : Number(e.target.value))}
                    style={{ width: 80, border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '7px 10px', fontSize: 13, outline: 'none', textAlign: 'center' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 6 }}>Grade Max (1–10)</label>
                  <input type="number" min={1} max={10} value={form.conditions.gradeMax ?? ''} onChange={e => setCond('gradeMax', e.target.value === '' ? '' : Number(e.target.value))}
                    style={{ width: 80, border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '7px 10px', fontSize: 13, outline: 'none', textAlign: 'center' }} />
                </div>
              </div>
            </div>
          )}

          {/* ── Sequences tab ── */}
          {tab === 'seqs' && (
            <SequenceEditor sequences={form.sequences} onChange={v => set('sequences', v)} />
          )}

          {/* ── Recommendations tab ── */}
          {tab === 'recs' && (
            <RecommEditor recommendations={form.recommendations} bodyPart={form.bodyPart} onChange={v => set('recommendations', v)} />
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: '2px solid #e8edf5', display: 'flex', justifyContent: 'flex-end', gap: 10, background: '#fafbfc', borderRadius: '0 0 20px 20px' }}>
          <button onClick={onClose} style={{ padding: '10px 22px', border: '1.5px solid #d1d5db', borderRadius: 10, background: '#fff', color: '#374151', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{
            padding: '10px 22px', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer',
            background: saving ? '#94a3b8' : 'linear-gradient(135deg, #1976d2, #0d47a1)', color: '#fff',
            boxShadow: saving ? 'none' : '0 3px 10px rgba(25,118,210,.35)',
          }}>{saving ? '⏳ Saving…' : '💾 Save Mapping'}</button>
        </div>
      </div>
    </div>
    </>
  );
}

// ── Generate confirm dialog ───────────────────────────────────────────────────
function GenerateDialog({ onConfirm, onClose, loading, result }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => e.target === e.currentTarget && !loading && onClose()}>
      <div style={{ background: '#fff', borderRadius: 20, padding: 32, maxWidth: 460, width: '90%', textAlign: 'center', boxShadow: '0 24px 60px rgba(0,0,0,.22)' }}>
        {!result ? (
          <>
            <div style={{ fontSize: 48, marginBottom: 12 }}>⚡</div>
            <h3 style={{ margin: '0 0 10px', fontSize: 18, fontWeight: 800 }}>Generate Mappings from Data</h3>
            <p style={{ color: '#6b7280', fontSize: 13, margin: '0 0 24px', lineHeight: 1.6 }}>
              This will analyze <strong>all patient consultation records</strong> and auto-generate (or refresh) one mapping per body part based on the most commonly assigned exercises and recommendations.
              <br /><br />
              Existing <strong>manual</strong> mappings are not affected. Auto-generated mappings will be updated.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button onClick={onClose} style={{ padding: '10px 22px', border: '1.5px solid #d1d5db', borderRadius: 10, background: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button onClick={onConfirm} disabled={loading} style={{
                padding: '10px 22px', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700,
                background: loading ? '#94a3b8' : 'linear-gradient(135deg, #f59e0b, #b45309)', color: '#fff', cursor: loading ? 'not-allowed' : 'pointer',
              }}>{loading ? '⏳ Generating…' : '⚡ Generate'}</button>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
            <h3 style={{ margin: '0 0 10px', fontSize: 18, fontWeight: 800 }}>Mappings Generated!</h3>
            <p style={{ color: '#6b7280', fontSize: 13, margin: '0 0 24px' }}>
              <strong>{result.created}</strong> body-part mappings created/updated from <strong>{result.totalPatients}</strong> patient records.
            </p>
            <button onClick={onClose} style={{ padding: '10px 28px', border: 'none', borderRadius: 10, background: '#1976d2', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Done</button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────
export default function MappingsDashboard() {
  const { dialogNode, showAlert, showConfirm } = useDialog();
  const [mappings,      setMappings]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [search,        setSearch]        = useState('');
  const [bodyPart,      setBodyPart]      = useState('all');
  const [activeFilter,  setActiveFilter]  = useState('all');   // 'all' | 'active' | 'inactive'
  const [sortKey,       setSortKey]       = useState('bodyPart_asc');
  const [selected,      setSelected]      = useState(new Set());
  const [editTarget,    setEditTarget]    = useState(null);     // null | {} for new | {_id,...} for edit
  const [showGenerate,  setShowGenerate]  = useState(false);
  const [genLoading,    setGenLoading]    = useState(false);
  const [genResult,     setGenResult]     = useState(null);

  const searchRef = useRef(null);

  // ── Fetch ──
  const fetchMappings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeFilter !== 'all') params.set('active', activeFilter === 'active' ? 'true' : 'false');
      const res = await api.get(`${process.env.REACT_APP_URL}/mappings?${params}`);
      setMappings(res.data || []);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  }, [activeFilter]);

  useEffect(() => { fetchMappings(); }, [fetchMappings]);

  // ── Derived / filtered list ──
  const displayed = sortMappings(
    mappings.filter(m => {
      if (bodyPart !== 'all' && m.bodyPart !== bodyPart) return false;
      if (search && !m.name.toLowerCase().includes(search.toLowerCase()) &&
          !(PART_LABELS[m.bodyPart] || '').toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    }),
    sortKey
  );

  const allDisplayedSelected = displayed.length > 0 && displayed.every(m => selected.has(m._id));

  // ── Handlers ──
  const toggleSelect = id => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll    = () => {
    if (allDisplayedSelected) setSelected(s => { const n = new Set(s); displayed.forEach(m => n.delete(m._id)); return n; });
    else setSelected(s => { const n = new Set(s); displayed.forEach(m => n.add(m._id)); return n; });
  };

  const handleSave = async (form) => {
    if (form._id) {
      const res = await api.put(`${process.env.REACT_APP_URL}/mappings/${form._id}`, form);
      setMappings(prev => prev.map(m => m._id === form._id ? res.data : m));
    } else {
      const res = await api.post(`${process.env.REACT_APP_URL}/mappings`, form);
      setMappings(prev => [res.data, ...prev]);
    }
  };

  const handleDelete = async (id) => {
    if (!await showConfirm('Delete this mapping?', { okLabel: 'Delete', danger: true })) return;
    await api.delete(`${process.env.REACT_APP_URL}/mappings/${id}`);
    setMappings(prev => prev.filter(m => m._id !== id));
    setSelected(s => { const n = new Set(s); n.delete(id); return n; });
  };

  const handleBulkDelete = async () => {
    if (!await showConfirm(`Delete ${selected.size} mapping(s)? This cannot be undone.`, { okLabel: 'Delete All', danger: true })) return;
    await api.delete(`${process.env.REACT_APP_URL}/mappings`, { data: { ids: [...selected] } });
    setMappings(prev => prev.filter(m => !selected.has(m._id)));
    setSelected(new Set());
  };

  const handleToggleActive = async (m) => {
    const res = await api.put(`${process.env.REACT_APP_URL}/mappings/${m._id}`, { ...m, isActive: !m.isActive });
    setMappings(prev => prev.map(x => x._id === m._id ? res.data : x));
  };

  const handleGenerate = async () => {
    setGenLoading(true);
    try {
      const res = await api.post(`${process.env.REACT_APP_URL}/mappings/generate`);
      setGenResult(res.data);
      await fetchMappings();
    } catch (e) {
      showAlert('Generation failed: ' + (e?.response?.data || e?.message), 'error');
    } finally { setGenLoading(false); }
  };

  const closeGenerate = () => { setShowGenerate(false); setGenResult(null); };

  // ── Body parts that actually have mappings ──
  const partsWithMappings = Object.keys(PART_LABELS).filter(p => mappings.some(m => m.bodyPart === p));

  return (
    <div style={{ padding: '20px 20px 80px', background: '#f0f4f8', minHeight: '100vh' }}>
      {dialogNode}

      {/* ── Page header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 900, color: '#111827', letterSpacing: -0.5 }}>
            Treatment Mappings
          </h1>
          <p style={{ margin: '3px 0 0', color: '#6b7280', fontSize: 13 }}>
            Body part rules → exercises & recommendations • {mappings.length} total
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => { setShowGenerate(true); setGenResult(null); }} style={{
            padding: '10px 18px', border: '1.5px solid #d97706', borderRadius: 12,
            background: '#fffbeb', color: '#b45309', fontSize: 13, fontWeight: 700, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>⚡ Generate from Data</button>
          <button onClick={() => setEditTarget({})} style={{
            padding: '10px 18px', border: 'none', borderRadius: 12,
            background: 'linear-gradient(135deg, #1976d2, #0d47a1)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
            boxShadow: '0 3px 10px rgba(25,118,210,.4)', display: 'flex', alignItems: 'center', gap: 6,
          }}>+ New Mapping</button>
        </div>
      </div>

      {/* ── Filter + search bar ── */}
      <div style={{ background: '#fff', borderRadius: 16, padding: '14px 18px', marginBottom: 14, border: '1.5px solid #e8edf5', boxShadow: '0 2px 8px rgba(0,0,0,.04)', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Row 1: search + sort + active filter */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
            <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 16 }}>🔍</span>
            <input ref={searchRef} value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search mappings, body parts…"
              style={{ width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '9px 12px 9px 36px', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
            {search && <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 16 }}>✕</button>}
          </div>
          <select value={sortKey} onChange={e => setSortKey(e.target.value)}
            style={{ border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '9px 12px', fontSize: 13, outline: 'none', background: '#fff', minWidth: 160 }}>
            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <div style={{ display: 'flex', border: '1.5px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
            {[{k:'all',l:'All'},{k:'active',l:'Active'},{k:'inactive',l:'Inactive'}].map(opt => (
              <button key={opt.k} onClick={() => setActiveFilter(opt.k)} style={{
                padding: '8px 14px', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: activeFilter === opt.k ? 700 : 400,
                background: activeFilter === opt.k ? '#1976d2' : '#fff',
                color: activeFilter === opt.k ? '#fff' : '#374151',
              }}>{opt.l}</button>
            ))}
          </div>
        </div>
        {/* Row 2: body part chips */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', marginRight: 4 }}>Part:</span>
          <button onClick={() => setBodyPart('all')} style={{
            padding: '4px 12px', borderRadius: 20, border: `1.5px solid ${bodyPart === 'all' ? '#1976d2' : '#d1d5db'}`,
            background: bodyPart === 'all' ? '#1976d2' : '#fff', color: bodyPart === 'all' ? '#fff' : '#374151',
            fontSize: 11, fontWeight: bodyPart === 'all' ? 700 : 400, cursor: 'pointer',
          }}>All</button>
          {partsWithMappings.map(p => {
            const active = bodyPart === p;
            return (
              <button key={p} onClick={() => setBodyPart(active ? 'all' : p)} style={{
                padding: '4px 12px', borderRadius: 20, border: `1.5px solid ${active ? partColor(p) : '#e2e8f0'}`,
                background: active ? partColor(p) : '#f8fafc', color: active ? '#fff' : '#374151',
                fontSize: 11, fontWeight: active ? 700 : 400, cursor: 'pointer',
              }}>{PART_LABELS[p]}</button>
            );
          })}
        </div>
      </div>

      {/* ── Bulk action bar ── */}
      {selected.size > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#1e293b', borderRadius: 12, padding: '12px 18px', marginBottom: 14, color: '#fff' }}>
          <span style={{ fontWeight: 700, fontSize: 14 }}>✓ {selected.size} selected</span>
          <div style={{ flex: 1 }} />
          <button onClick={handleBulkDelete} style={{ padding: '7px 16px', border: '1.5px solid #fca5a5', borderRadius: 8, background: 'transparent', color: '#fca5a5', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            🗑 Delete Selected
          </button>
          <button onClick={() => setSelected(new Set())} style={{ padding: '7px 14px', border: '1.5px solid #64748b', borderRadius: 8, background: 'transparent', color: '#94a3b8', fontSize: 12, cursor: 'pointer' }}>
            Clear
          </button>
        </div>
      )}

      {/* ── Select-all bar ── */}
      {displayed.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, paddingLeft: 4 }}>
          <input type="checkbox" checked={allDisplayedSelected} onChange={toggleAll}
            style={{ width: 16, height: 16, accentColor: '#1976d2', cursor: 'pointer' }} />
          <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>
            {allDisplayedSelected ? 'Deselect all' : `Select all ${displayed.length} shown`}
          </span>
          <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 'auto' }}>{displayed.length} mapping{displayed.length !== 1 ? 's' : ''}</span>
        </div>
      )}

      {/* ── Grid ── */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '80px 0', color: '#9ca3af', fontSize: 16 }}>⏳ Loading mappings…</div>
      ) : displayed.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px 20px', color: '#9ca3af' }}>
          <div style={{ fontSize: 52, marginBottom: 12 }}>🗂️</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#374151', marginBottom: 8 }}>No mappings found</div>
          <div style={{ fontSize: 13, marginBottom: 20 }}>
            {search || bodyPart !== 'all'
              ? 'Try adjusting your search or filters'
              : 'Generate from consultation data or create your first mapping manually'}
          </div>
          {(!search && bodyPart === 'all') && (
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button onClick={() => { setShowGenerate(true); setGenResult(null); }} style={{ padding: '10px 20px', border: 'none', borderRadius: 10, background: '#f59e0b', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>⚡ Generate from Data</button>
              <button onClick={() => setEditTarget({})} style={{ padding: '10px 20px', border: 'none', borderRadius: 10, background: '#1976d2', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>+ New Mapping</button>
            </div>
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
          {displayed.map(m => (
            <MappingCard
              key={m._id} m={m}
              selected={selected.has(m._id)}
              onSelect={() => toggleSelect(m._id)}
              onEdit={() => setEditTarget(m)}
              onDelete={() => handleDelete(m._id)}
              onToggleActive={() => handleToggleActive(m)}
            />
          ))}
        </div>
      )}

      {/* ── Modals ── */}
      {editTarget !== null && (
        <MappingModal mapping={editTarget?._id ? editTarget : null} onClose={() => setEditTarget(null)} onSave={handleSave} />
      )}
      {showGenerate && (
        <GenerateDialog onConfirm={handleGenerate} onClose={closeGenerate} loading={genLoading} result={genResult} />
      )}
    </div>
  );
}
